import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import moment from "moment";
import { LEAVE_BALANCE_API } from "../../service/apiUrl";
import { postApiCall } from "../../service/PostApiCall";
import DataTable from "../../components/table/DataTable";
import CustomModal from "../../components/CustomModal";

/* The balance is the backend's ledger (backend/helper/LeaveBalance.php): one
   paid leave credited per month, unused leave carried into the next month,
   the carry reset every January, approved leave drawn from it in date order.
   This screen only displays it — it used to rebuild the numbers itself from
   one leave-manage request per employee, and read `user.leave_balance`, a
   column nothing ever wrote. */

const currentYear = moment().year();
const yearOptions = Array.from({ length: 5 }, (_, i) => {
  const year = String(currentYear - i);
  return { value: year, label: year };
});

const ManageLeaveBalance = (props) => {
  const signdata = useSelector((state) => state.signinReducers);
  const access_token = signdata?.data?.access_token;
  const [year, setYear] = useState(yearOptions[0]);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [breakdown, setBreakdown] = useState(null);

  useEffect(() => {
    if (!access_token) return;
    postApiCall({ year: year.value }, access_token, LEAVE_BALANCE_API)
      .then((response) => {
        setRows(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        console.error("LEAVE_BALANCE", error);
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [access_token, year]);

  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "S.No",
        enableSorting: false,
        enableHiding: false,
        /* `row.index` is the position in the unsorted source array, so it
           renumbers nothing when the table is sorted or filtered and repeats
           itself across pages. `getDisplayIndex()` is the row's place in what
           the user is actually looking at. */
        cell: ({ row }) => (
          <span className="text-nowrap">{row.getDisplayIndex() + 1}</span>
        ),
      },
      {
        id: "employee_name",
        accessorFn: (row) =>
          `${row?.first_name ?? ""} ${row?.last_name ?? ""}`.trim(),
        header: "Name",
        enableHiding: false,
        cell: ({ row }) => (
          <span className="text-nowrap fw-medium">
            {row.original?.first_name} {row.original?.last_name}
          </span>
        ),
      },
      {
        /* `credited` only counts months up to now — the backend skips later
           ones — so it is the leave accumulated so far, not the year's 12. */
        accessorKey: "credited",
        header: "Total Leaves",
        cell: ({ row }) => (
          <span className="text-nowrap">{row.original?.credited ?? 0}</span>
        ),
      },
      {
        accessorKey: "used",
        header: "Used",
        cell: ({ row }) => (
          <span className="text-nowrap">
            {row.original?.used ?? 0}
          </span>
        ),
      },
      {
        accessorKey: "unpaid",
        header: "Unpaid",
        cell: ({ row }) => (
          <span
            className={`text-nowrap ${
              row.original?.unpaid > 0 ? "text-danger" : ""
            }`}
          >
            {row.original?.unpaid ?? 0}
          </span>
        ),
      },
      {
        accessorKey: "leave_balance",
        header: "Balance",
        cell: ({ row }) => (
          <span
            className={`text-nowrap fw-medium ${
              Number(row.original?.leave_balance) > 0
                ? "text-success"
                : "text-danger"
            }`}
          >
            {row.original?.leave_balance ?? 0}
          </span>
        ),
      },
      {
        id: "breakdown",
        header: "",
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <button
            type="button"
            className="dt-btn"
            onClick={() => setBreakdown(row.original)}
          >
            <i className="fa fa-list" aria-hidden="true"></i>
            By month
          </button>
        ),
      },
    ],
    []
  );

  return (
    <div className={props?.data?.transtion == true ? "contents" : "content"}>
      <div className="content-top-spacer"></div>
      <div className="color-body">
        <div className="page-head">
          <div className="page-eyebrow">People</div>
          <h1 className="page-title">Leave Balance</h1>
          <p className="page-sub">
            One paid leave is credited each month. Unused leave carries
            forward and resets in January.
          </p>
        </div>
        <div className="mt-4">
          <div className="card-input mt-3">
            <div className="col-md-12 mb-3">
              <div className="mt-3">
                <DataTable
                  columns={columns}
                  data={rows}
                  isLoading={!loaded}
                  getRowId={(row, index) => String(row?.id ?? index)}
                  search={{
                    columns: ["employee_name"],
                    placeholder: "Search employee...",
                  }}
                  filters={
                    <div className="listing-filters">
                      <div className="filter-cell">
                        <label className="filter-label">Year</label>
                        <Select
                          classNamePrefix="rs"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          className="mt-2 mb-2"
                          options={yearOptions}
                          isSearchable={false}
                          value={year}
                          onChange={(option) => option && setYear(option)}
                        />
                      </div>
                    </div>
                  }
                  pagination={{
                    pageSize: 25,
                    pageSizeOptions: [10, 25, 50, 100],
                  }}
                  emptyText="No record found"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        isShow={Boolean(breakdown)}
        onHide={() => setBreakdown(null)}
        heading={
          breakdown
            ? `${breakdown.first_name ?? ""} ${breakdown.last_name ?? ""} · ${
                breakdown.year
              }`.trim()
            : ""
        }
        description="One paid leave is added each month and approved leave is paid from what has built up. Anything beyond it is unpaid."
      >
        {breakdown?.months?.length ? (
          <div className="table-responsive">
            <table className="table table-vcenter card-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Leaves</th>
                  <th>Used</th>
                  <th>Unpaid</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.months.map((month) => (
                  <tr key={month.month}>
                    <td className="fw-medium">{month.label}</td>
                    <td>{month.credited}</td>
                    <td>{month.used}</td>
                    <td className={month.unpaid > 0 ? "text-danger" : ""}>
                      {month.unpaid}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted mb-0">Nothing credited in this year yet.</p>
        )}
      </CustomModal>
    </div>
  );
};

export default ManageLeaveBalance;
